import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNotification } from "../notification.hook";
import CmSheetFeedbacksService from "../../services/api/cmSheet/CmSheetFeedbacks.service";
import {
  DATA_SAVED_SUCCESS_LABEL,
  ERROR_LABEL,
  ERROR_MESSAGE_LABEL,
  SEND_ADMIN_COMMENT_DEVELOPMENT_LABEL,
} from "../../constants/Dictionary";
import { UpdateCmSheetAdminFeedbacksRequest } from "../../types/api/collaborators.type";
import { RefreshReactQuery } from "../../constants/AppConstants";
import { useEffect } from "react";
import { CM_SHEET_STATE_MACHINE_QUERY_KEY } from "./stateMachine.hook";

const ADMIN_COMMENTS_STATS_QUERY_KEY = "get-cm-sheet-admin-feedbacks";

export const useCmSheetAdminFeedbacks = (cmSheetId: number, enabled = true) => {
  const { notification } = useNotification();
  const { data, isLoading, isFetching, isError } = useQuery({
    queryKey: [ADMIN_COMMENTS_STATS_QUERY_KEY, cmSheetId],
    queryFn: () => CmSheetFeedbacksService.get(cmSheetId),
    staleTime: RefreshReactQuery.MODERATE,
    enabled: enabled,
  });

  useEffect(() => {
    if (isError) {
      notification.error(ERROR_LABEL, ERROR_MESSAGE_LABEL);
    }
  }, [isError]);

  return { data, isLoading, isFetching };
};

export const useUpdateCmSheetAdminFeedbacks = (cmSheetId: number) => {
  const queryClient = useQueryClient();
  const { notification } = useNotification();

  const { mutateAsync, isPending } = useMutation({
    mutationKey: ["update-admin-feedbacks", cmSheetId],
    mutationFn: (body: UpdateCmSheetAdminFeedbacksRequest) =>
      CmSheetFeedbacksService.update(cmSheetId, body),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: [ADMIN_COMMENTS_STATS_QUERY_KEY],
        }),
        queryClient.invalidateQueries({
          queryKey: [CM_SHEET_STATE_MACHINE_QUERY_KEY],
        }),
      ]);
      notification.success(DATA_SAVED_SUCCESS_LABEL, undefined, "toast");
    },
    onError: (error) => {
      console.error(`errore update admin feedbacks ${error}`);
      notification.error(ERROR_LABEL, ERROR_MESSAGE_LABEL);
    },
  });

  return { mutateAsync, isPending };
};

export const useSendAdminComments = (cmSheetId: number) => {
  const queryClient = useQueryClient();
  const { notification } = useNotification();

  const { mutateAsync, isPending } = useMutation({
    mutationKey: ["send-admin-comments", cmSheetId],
    mutationFn: () =>
      CmSheetFeedbacksService.submitDevelopmentComment(cmSheetId),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: [ADMIN_COMMENTS_STATS_QUERY_KEY],
        }),
        queryClient.invalidateQueries({
          queryKey: [CM_SHEET_STATE_MACHINE_QUERY_KEY],
        }),
      ]);
      notification.success(
        SEND_ADMIN_COMMENT_DEVELOPMENT_LABEL,
        undefined,
        "toast",
      );
    },
    onError: (error) => {
      console.error(`error sending admin comments ${error}`);
      notification.error(ERROR_LABEL, ERROR_MESSAGE_LABEL);
    },
  });

  return { mutateAsync, isPending };
};
